import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Download, PenSquare, Plus } from "lucide-react";
import { api, errorMessage, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { UploadDropzone } from "@/components/editor/UploadDropzone";
import { SourceAnalysis } from "@/components/editor/SourceAnalysis";
import { PreferencesPanel } from "@/components/editor/PreferencesPanel";
import { BatchProgress } from "@/components/editor/BatchProgress";
import { VariationCard } from "@/components/editor/VariationCard";
import { VariationEditDialog } from "@/components/editor/VariationEditDialog";
import { VariantCarousel } from "@/components/editor/VariantCarousel";

const DEFAULT_PREFS = { platforms: ["tiktok", "reels", "shorts"], hook_styles: [], caption_styles: [], pacings: [], framings: [], count: 50, smart_framing: true };
const FILTERS = ["all", "rendered", "approved", "failed", "near-duplicates", "processing"];

export default function Editor() {
  const { projectId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [project, setProject] = useState(null);
  const [batch, setBatch] = useState(null);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [capability, setCapability] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [wantNewBatch, setWantNewBatch] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [voices, setVoices] = useState([]);
  const batchId = params.get("batch");
  const timer = useRef(null);

  useEffect(() => { api.get("/tts/voices").then((r) => setVoices(r.data.voices)).catch(() => {}); }, []);

  const loadProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}`);
      setProject(data);
      if (!batchId && !wantNewBatch && data.batches?.length) setParams({ batch: data.batches[0].id }, { replace: true });
    } catch (e) {
      toast.error(errorMessage(e));
      navigate("/editor");
    }
  }, [projectId, batchId, wantNewBatch, navigate, setParams]);

  const loadBatch = useCallback(async () => {
    if (!batchId) return;
    try {
      const { data } = await api.get(`/batches/${batchId}`);
      setBatch(data);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [batchId]);

  useEffect(() => { loadProject(); }, [loadProject]);
  useEffect(() => { setBatch(null); loadBatch(); }, [loadBatch]);

  useEffect(() => {
    const projectBusy = project?.status === "analyzing";
    const batchBusy = batch && ["planning", "rendering"].includes(batch.status);
    const anyProcessing = batch?.variations?.some((v) => ["queued", "processing"].includes(v.status));
    if (!projectBusy && !batchBusy && !anyProcessing) return;
    timer.current = setTimeout(() => { if (projectBusy) loadProject(); else loadBatch(); }, 2500);
    return () => clearTimeout(timer.current);
  }, [project, batch, loadProject, loadBatch]);

  useEffect(() => {
    if (!project || project.status !== "ready") return;
    api.post(`/projects/${project.id}/capability`, prefs).then((r) => setCapability(r.data)).catch(() => {});
  }, [project, prefs]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/batches`, prefs);
      toast.success(`Batch of ${data.count} queued`);
      setWantNewBatch(false);
      setParams({ batch: data.id });
      refreshUser();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setGenerating(false);
    }
  };

  const patchVariation = async (v, body, okMsg) => {
    try {
      const { data } = await api.patch(`/variations/${v.id}`, body);
      setBatch((b) => ({ ...b, variations: b.variations.map((x) => (x.id === data.id ? data : x)) }));
      if (okMsg) toast.success(okMsg);
      if (body.rerender) { loadBatch(); refreshUser(); }
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const regenerate = async (v) => {
    try {
      const { data } = await api.post(`/variations/${v.id}/regenerate`);
      setBatch((b) => ({ ...b, status: "rendering", variations: b.variations.map((x) => (x.id === data.id ? data : x)) }));
      toast.success(`Regenerating #${v.index + 1} with a new moment`);
      refreshUser();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const download = async (v) => {
    try {
      const { data } = await api.get(`/variations/${v.id}/download`);
      window.open(`${BACKEND_URL}${data.url}`, "_blank");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const exportZip = async (scope) => {
    setExporting(true);
    const id = toast.loading(scope === "all" ? "Packing all rendered variations…" : "Packing approved variations…");
    try {
      const { data } = await api.post(`/batches/${batch.id}/export?scope=${scope}`);
      toast.success(`${data.count} files zipped (${(data.size_bytes / 1e6).toFixed(1)} MB)`, { id });
      window.open(`${BACKEND_URL}${data.url}`, "_blank");
    } catch (e) {
      toast.error(errorMessage(e), { id });
    } finally {
      setExporting(false);
    }
  };

  const visible = useMemo(() => {
    const list = batch?.variations || [];
    return list.filter((v) => filter === "all" || (filter === "approved" ? v.approved : filter === "near-duplicates" ? v.near_duplicate_of : filter === "processing" ? ["queued", "processing"].includes(v.status) : v.status === filter));
  }, [batch, filter]);

  if (!projectId) {
    return (
      <div className="max-w-3xl space-y-6 rise">
        <div><span className="overline-label">Editor</span><h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">New repurposing project</h1></div>
        <UploadDropzone onUploaded={(p) => navigate(`/editor/${p.id}`)} />
        <p className="text-sm text-muted-foreground">Previous projects live in <Link to="/history" className="text-primary hover:underline">History</Link>.</p>
      </div>
    );
  }
  if (!project) return <div className="h-64 rounded-2xl shimmer" />;

  return (
    <div className="space-y-6 rise" data-testid="editor-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><span className="overline-label">Editor</span><h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Batch studio</h1></div>
        <div className="flex gap-2">
          {batch && (
            <Button onClick={() => exportZip((batch.counts?.approved || 0) > 0 ? "approved" : "all")} disabled={exporting || !(batch.counts?.rendered > 0)} className="btn-pill" data-testid="export-zip-button">
              <Download size={15} className="mr-2" /> {(batch.counts?.approved || 0) > 0 ? `Export ${batch.counts.approved} approved` : `Export all ${batch.counts?.rendered || 0}`} (.zip)
            </Button>
          )}
          {batch && <Button asChild variant="outline" className="btn-pill" data-testid="go-compose-button"><Link to={`/compose/${batch.id}`}><PenSquare size={15} className="mr-2" /> Compose <ArrowRight size={14} className="ml-1" /></Link></Button>}
          <Button asChild variant="ghost" className="btn-pill" data-testid="new-project-button"><Link to="/editor"><Plus size={15} className="mr-1" /> New</Link></Button>
        </div>
      </div>
      {batch && <VariantCarousel project={project} batch={batch} onNewBatch={() => { setWantNewBatch(true); setBatch(null); setParams({}); }} />}
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-6">
          <SourceAnalysis project={project} capability={capability} />
          {project.batches?.length > 1 && (
            <div className="card-surface p-4 space-y-2" data-testid="batch-list">
              <span className="overline-label">Batches</span>
              {project.batches.map((b) => (
                <button key={b.id} onClick={() => setParams({ batch: b.id })} className={`w-full text-left rounded-xl px-3 py-2 text-sm transition-colors ${b.id === batchId ? "bg-primary/10 text-primary" : "hover:bg-muted"}`} data-testid={`batch-select-${b.id}`}>
                  {b.count} variations · <span className="font-mono text-xs">{b.status.replace(/_/g, " ")}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-8 space-y-6">
          {!batch && <PreferencesPanel prefs={prefs} setPrefs={setPrefs} onGenerate={generate} disabled={project.status !== "ready"} generating={generating} credits={user?.credits} />}
          {!batch && wantNewBatch && project.batches?.length > 0 && (
            <button onClick={() => { setWantNewBatch(false); setParams({ batch: project.batches[0].id }); }} className="text-xs text-primary hover:underline" data-testid="back-to-batch-button">← Back to latest batch</button>
          )}
          {batch && (
            <>
              <BatchProgress batch={batch} />
              <div className="flex flex-wrap items-center gap-2" data-testid="variation-filters">
                {FILTERS.map((f) => <button key={f} onClick={() => setFilter(f)} className={`chip text-xs ${filter === f ? "chip-active" : "chip-idle"}`} data-testid={`filter-${f}`}>{f}</button>)}
                <button onClick={() => { setWantNewBatch(true); setBatch(null); setParams({}); }} className="ml-auto text-xs text-primary hover:underline" data-testid="new-batch-button">+ New batch from this source</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="variation-grid">
                {visible.map((v) => (
                  <VariationCard key={v.id} v={v} onApprove={(x) => patchVariation(x, { approved: !x.approved })} onEdit={setEditing} onRegenerate={regenerate} onDownload={download} onRetry={(x) => patchVariation(x, { rerender: true }, "Retry queued")} />
                ))}
                {visible.length === 0 && <p className="text-sm text-muted-foreground col-span-full py-10 text-center" data-testid="empty-filter">Nothing matches this filter yet.</p>}
              </div>
            </>
          )}
        </div>
      </div>
      <VariationEditDialog variation={editing} maxDuration={project.meta.duration} voices={voices} onClose={() => setEditing(null)} onSave={async (body) => { await patchVariation(editing, body, body.rerender ? "Re-render queued" : "Saved"); setEditing(null); }} />
    </div>
  );
}
